import axios, {CancelToken} from "axios";

/**
 * Registro de una solicitud en vuelo.
 * `source` permite cancelar la petición anterior con la misma key.
 */
export type CancelEntry = {
    source: any; // axios.CancelTokenSource (en axios 0.19 no tipado perfecto)
    ts: number;
};

/**
 * Requests en vuelo indexados por la key de deduplicación.
 */
const inFlightRequests = new Map<string, CancelEntry>();

/**
 * Opciones para construir el fingerprint estable del body.
 */
type FingerprintOptions = {
    maxDepth?: number;
    maxKeys?: number;
    maxArray?: number;
    maxBytes?: number;
    maxStringChars?: number;
};

const DEFAULT_FP_OPTS: Required<FingerprintOptions> = {
    maxDepth: 8,
    maxKeys: 200,
    maxArray: 200,
    maxBytes: 150_000,
    maxStringChars: 256,
};

/**
 * FNV-1a 32-bit (rápido y suficiente para dedupe de requests).
 */
const FNV_OFFSET = 0x811c9dc5;
const FNV_PRIME = 0x01000193;

const fnv1a = (input: string): number => {
    let hash = FNV_OFFSET >>> 0;
    for (let i = 0; i < input.length; i++) {
        hash ^= input.charCodeAt(i);
        hash = (hash * FNV_PRIME) >>> 0;
    }
    return hash >>> 0;
};

const compactString = (value: string, maxChars: number): string => {
    if (value.length <= maxChars) return value;
    const edge = Math.floor(maxChars / 2);
    return `__str:${value.length}:${value.slice(0, edge)}:${value.slice(-edge)}`;
};

/**
 * Normaliza el body para que sea serializable y estable:
 * - Ordena keys de objetos.
 * - Trata `null` y `undefined` como equivalentes.
 * - Limita profundidad, cantidad de keys e items.
 * - Detecta ciclos.
 */
const normalizeForFingerprint = (
    value: any,
    opts: Required<FingerprintOptions>,
    depth: number,
    seen: WeakSet<object>,
): any => {
    if (depth >= opts.maxDepth) return "__maxDepth";
    if (value === undefined || value === null) return null;

    const type = typeof value;
    if (type === "string") return compactString(value, opts.maxStringChars);
    if (type === "number") {
        if (Number.isNaN(value)) return "__NaN";
        if (Object.is(value, -0)) return "__-0";
        return value;
    }
    if (type === "boolean") return value;
    if (type === "bigint") return `__bigint:${value.toString()}`;
    if (type === "symbol") return "__symbol";
    if (type === "function") return "__function";

    if (value instanceof Date) return `__date:${value.toISOString()}`;

    if (typeof value === "object") {
        if (seen.has(value)) return "__cycle";
        seen.add(value);

        if (Array.isArray(value)) {
            const limit = Math.min(value.length, opts.maxArray);
            const normalizedItems = value.slice(0, limit).map((item) => (
                normalizeForFingerprint(item, opts, depth + 1, seen)
            ));
            if (value.length > limit) {
                normalizedItems.push(`__moreItems:${value.length - limit}`);
            }
            return normalizedItems;
        }

        const keys = Object.keys(value).sort((a, b) => a.localeCompare(b));
        const limit = Math.min(keys.length, opts.maxKeys);
        const normalizedObject: Record<string, any> = {};
        for (let i = 0; i < limit; i++) {
            const key = keys[i];
            normalizedObject[key] = normalizeForFingerprint(value[key], opts, depth + 1, seen);
        }
        if (keys.length > limit) {
            normalizedObject.__moreKeys = keys.length - limit;
        }
        return normalizedObject;
    }

    return String(value);
};

/**
 * Devuelve un fingerprint estable para bodies "equivalentes".
 * Dos objetos con mismo contenido (aunque cambie el orden de keys)
 * terminan con la misma huella.
 */
function stableFingerprint(value: any, options?: FingerprintOptions): string {
    const opts = { ...DEFAULT_FP_OPTS, ...(options || {}) };
    const normalized = normalizeForFingerprint(value, opts, 0, new WeakSet<object>());
    let canonical = JSON.stringify(normalized);

    if (canonical.length > opts.maxBytes) {
        canonical = `${canonical.slice(0, opts.maxBytes)}|__truncated:${canonical.length}`;
    }

    return fnv1a(canonical).toString(36);
}


/**
 * Construye la key usada para deduplicar/cancelar requests:
 * `method | url | fingerprint(body)`.
 */
export const buildRequestKey = (method: string, baseURL?: string, service?: string, data?: any): string => {
    const url = `${baseURL || ""}${service || ""}`;
    const bodyFp = stableFingerprint(data, {
        maxDepth: 8,
        maxKeys: 250,
        maxArray: 250,
        maxBytes: 120_000,
    });

    return [method.toLowerCase(), url, bodyFp].join("|");
};

/**
 * Registra el request en vuelo y cancela el anterior con la misma key.
 */
export const attachCancelToken = (key: string): CancelToken => {
    const prev = inFlightRequests.get(key);
    if (prev) prev.source.cancel(`Solicitud anterior cancelada: ${key}`);

    const source = axios.CancelToken.source();
    inFlightRequests.set(key, { source, ts: Date.now() });

    return source.token;
};

/**
 * Limpia la key del mapa cuando el request termina.
 */
export const cleanupCancelToken = (key: string): void => {
    inFlightRequests.delete(key);
};
